import { Component } from '@mycolorway/tao';
import { closest, matches } from 'next/javascripts/modules/dom';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import baseGrid from './base';
import todoGridSelectable from './todo-grid-selectable';
import todoGridSortable from './todo-grid-sortable';
import columnResizable from './column-resizable';
import 'next/stylesheets/components/grid/todo-grid.scss';

const SLIDE_PAGE_WIDTH = window.matchMedia('(min-width: 1920px)').matches ? 960 : 708;
const MINIMAL_COLUMNS_MIN_WIDTH = 132 + 64; // 截止日期/负责人

Component('tr-todo-grid', {
  mixins: [
    columnResizable, todoGridSelectable, todoGridSortable,
    baseGrid, eventListenerMixin,
  ],

  properties: {
    hiddenColumnKeys: {
      type: Array,
      default: [],
      observer: 'hiddenColumnKeysChanged',
    },

    minimal: {
      type: Boolean,
      default: false,
      observer() {
        this.broadcastSubjectEvent('minimalChanged');
      },
    },

    stickyColumnMinWidthOnMinimal: {
      type: Number,
      default: 380,
    },

    slideOpened: {
      type: Boolean,
      default: false,
    },

    forwardTodo: {
      type: Object,
    },

    openDetailsBySlidePage: {
      type: Boolean,
    },

    currentSelectedRow: {
      get() {
        return this.selectedRows[this.selectedRows.length - 1];
      },

      set(row) {
        if (row) {
          const rowIndex = this.selectedRows.indexOf(row);
          if (rowIndex === -1) this.selectedRows.push(row);
        } else {
          this.selectedRows.splice(-1, 1);
        }

        return row;
      },
    },
  },

  connected() {
    this.selectedRows = [...this.querySelectorAll('tr-grid-todo-row[selected]')];

    this.slidePage = this.querySelector('tr-slide-page');
    this.dialogPage = this.querySelector('tr-dialog-page');

    if (this.slidePage.opened) this.updateColumns();
    this.batchOperationBar = this.querySelector('tr-project-list-view-batch-operation-bar');

    this.listenTo(this, {
      'tr-grid-todo-group:afterConnected': this.handleGroupConnected.bind(this),
      'tr-grid-todo-group:expandedChange': this.handleGroupExpanded.bind(this),
      'tr-grid-todo-group:newTodoGroupCreated': this.handleGroupCreated.bind(this),
      'tr-grid-todo-row:afterConnected': this.handleRowConnected.bind(this),
      'tr-grid-todo-row:beforeDisconnected': ({ target }) => {
        if (this.currentSelectedRow && this.currentSelectedRow === target) {
          this.currentSelectedRow = null;
        }
      },
      'tr-grid-todo-row:open': (event) => {
        const { detail: { detailUrl } } = event;
        this.openRowDetail({ detailUrl });
      },
      'tr-todo-grid:rowSortableEnd': () => {
        this.querySelectorAll('tr-grid-todo-group').forEach((item) => {
          item.detectEmpty();
          item.summary();
        });
      },
      'tr-grid-summary-cell:change': (e) => {
        const cell = e.target;
        const group = closest(cell, '.grid-group');
        this.querySelectorAll('.grid-group').forEach((g) => {
          if (g !== group) {
            g.updateSummaryFunc(cell.target, cell.funcName);
          }
        });
      },
    });

    this.listenTo(this.slidePage, {
      'tr-slide-page:beforeOpen': this.handleSlidePageOpened.bind(this),
      'tr-slide-page:beforeClose': this.handleSlidePageBeforeClose.bind(this),
    });

    this.listenTo(this.batchOperationBar, {
      'tr-project-list-view-batch-operation-bar:close': this.clearSelectedRows.bind(this),
    });

    this.listenTo(document, 'keydown', this.handleKeydown.bind(this));
  },

  ready() {
    this.navigateToForwardTodo();

    [...this.querySelectorAll('tr-grid-todo-group')]
      .forEach(group => group.initIntersectionObserver());
  },

  navigateToForwardTodo() {
    if (!this.forwardTodo
      || (!this.forwardTodo.url && !this.forwardTodo.guid)) return;

    const { guid, url } = this.forwardTodo;
    const forwardTodo = this.querySelector(`tr-grid-todo-row[guid="${guid}"]`);

    if (forwardTodo) {
      forwardTodo.selected = true;
      this.selectRow(forwardTodo);
      this.openRowDetail(forwardTodo);
      setTimeout(() => this.scrollIntoRow(forwardTodo));
    } else if (url) {
      this.openRowDetail({ detailUrl: url });
    }
  },

  getStickyColumnMinWidth() {
    return this.minimal
      ? this.stickyColumnMinWidthOnMinimal : this.stickyColumnMinWidth;
  },

  hiddenColumnKeysChanged(hiddenColumnKeys) {
    const groups = this.querySelectorAll('.grid-group');

    Array.from(this.headerRow.children).forEach((column) => {
      const { columnKey } = column.dataset;
      const hidden = hiddenColumnKeys.indexOf(columnKey) !== -1;
      column.classList.toggle('hidden', hidden);
      groups.forEach(group => group.toggleSummaryCell(columnKey, hidden));
    });

    this.broadcastSubjectEvent('hiddenColumnKeysChanged', {
      hiddenColumnKeys,
    });

    const width = this.calcStickyColumnMaxWidth();
    this.updateStickyColumnRange({ max: width });
    if (this.minimal) {
      this.resizableStickyColumnHeader.resize(width);
    }
  },

  removeEmptyTodos() {
    const emptyTodos = [...this.querySelectorAll('tr-grid-todo-row[editing]')].filter(
      todo => !todo.querySelector('tr-grid-todo-title-cell textarea').value,
    );
    emptyTodos.forEach(emptyTodo => emptyTodo.remove());
  },

  insertNewTodoRow({
    group,
    row,
    beforeRow,
    afterRow,
    endOfGroup,
  }) {
    const { currentSelectedRow } = this;

    if (beforeRow) {
      beforeRow.insertAdjacentElement('afterend', row);
    } else if (afterRow) {
      afterRow.insertAdjacentElement('beforebegin', row);
    } else if (group) {
      if (currentSelectedRow && group.contains(currentSelectedRow)) {
        currentSelectedRow.insertAdjacentElement('afterend', row);
      } else {
        group.querySelector('.group-content')
          .insertAdjacentElement(endOfGroup ? 'beforeend' : 'afterbegin', row);
      }
    } else if (currentSelectedRow
      && !closest(currentSelectedRow, '.grid-group', this)) {
      currentSelectedRow.insertAdjacentElement('afterend', row);
    } else {
      this.querySelector('.grid-content').insertAdjacentElement('afterbegin', row);
    }

    this.scrollToNewRowIfNeeded(row);
  },

  moveTodoIntoGroup(todo, group) {
    if (group) {
      group.querySelector('.group-content').insertAdjacentElement('beforeend', todo);
      return;
    }

    const firstGroup = this.querySelectorAll('.grid-content .grid-group')[0];

    if (firstGroup) {
      firstGroup.insertAdjacentElement('beforebegin', todo);
    } else {
      this.querySelector('.grid-content').insertAdjacentElement('beforeend', todo);
    }
  },

  clearSelectedRows() {
    this.selectedRows.splice(0, this.selectedRows.length)
      .forEach((selectedRow) => {
        // eslint-disable-next-line no-param-reassign
        selectedRow.selected = false;
      });
  },

  clearSelectedRowsExceptCurrent() {
    this.selectedRows.splice(0, this.selectedRows.length - 1)
      .forEach((selectedRow) => {
        // eslint-disable-next-line no-param-reassign
        selectedRow.selected = false;
      });
  },

  handleGroupConnected(event) {
    if (this.taoStatus !== 'ready') return;

    const group = event.target;
    group.updateGroupTitleWidth(parseFloat(this.stickyColumnStyle.width));
  },

  handleRowConnected(event) {
    if (this.taoStatus !== 'ready') return;

    const row = event.target;
    row.hiddenColumnKeys = this.hiddenColumnKeys;
    this.resiableColmunHeaders.forEach((item) => {
      row.updateColumnWidth({ key: item.syncKey, width: item.width });
    });
    if (this.minimal) row.toggleCellsMinimalStatus(true);

    const selectedRowIndex = this.selectedRows
      .findIndex(selectedRow => row.guid === selectedRow.guid);
    if (selectedRowIndex !== -1) {
      row.selected = true;
      this.selectedRows.splice(selectedRowIndex, 1, row);
    }
  },

  scrollToNewRowIfNeeded(row) {
    const group = closest(row, '.grid-group', this);
    const { top: rowTop, height: rowHeight } = row.getBoundingClientRect();

    if (group) {
      if (!group.headerSticky) return;

      const groupHeaderHeight = group.header.getBoundingClientRect().height;
      this.verticalContainer
        .scrollTop = group.offsetTop + groupHeaderHeight - rowHeight - 1;
    } else if (this.headerSticky) {
      const { top } = this.getBoundingClientRect();
      if (rowTop <= top) {
        this.verticalContainer
          .scrollTop = this.verticalContainer.scrollTop - rowHeight;
      }
    }
  },

  handleSlidePageOpened() {
    this.checkIfShouldBeMinimal();
    this.slideOpened = true;

    setTimeout(this.updateColumns.bind(this), 0);
  },

  updateColumns() {
    if (this.minimal) {
      this.hideExtraColumnsOnMinimal();
    }
  },

  checkIfShouldBeMinimal() {
    const containerNewWidth = this.horizontalContainer
      .getBoundingClientRect().width - SLIDE_PAGE_WIDTH;
    const minWidth = this.resizableStickyColumnHeader.width + this
      .getUnstickyColumnHeaders().slice(0, 3)
      .reduce((total, col) => total + col.width, 0);

    this.minimal = containerNewWidth < minWidth;
  },

  hideExtraColumnsOnMinimal() {
    const hiddenColumnKeys = [];

    this.getUnstickyColumnHeaders().forEach((column) => {
      const { columnKey } = column.dataset;
      if (columnKey && columnKey !== 'assignee_guid' && columnKey !== 'due_date') {
        hiddenColumnKeys.push(columnKey);
      }
    });

    ['assignee_guid', 'due_date'].forEach((key) => {
      const header = Array.from(this.resiableColmunHeaders)
        .find(item => item.syncKey === key);
      if (!header) return;
      if (key === 'assignee_guid') {
        header.resize(64);
      } else {
        header.resize(132);
      }
    });

    this.originalHiddenColumnKeys = [];
    this.hiddenColumnKeys = hiddenColumnKeys;
  },

  handleSlidePageBeforeClose(event) {
    const { trigger } = event.detail;
    if (trigger && (
      closest(trigger, '.grid-cell, .grid-sticky-cell', this)
      || !closest(trigger, 'body > .wrapper')
    )) {
      event.preventDefault();
      return;
    }

    const { minimal } = this;
    this.slideOpened = false;
    this.minimal = false;

    setTimeout(() => {
      if (this.originalHiddenColumnKeys) {
        this.hiddenColumnKeys = this.originalHiddenColumnKeys;
        this.originalHiddenColumnKeys = null;
      }

      if (minimal) {
        this.resizableStickyColumnHeader.restore();
        ['assignee_guid', 'due_date'].forEach((key) => {
          const header = Array.from(this.resiableColmunHeaders).find(item => item.syncKey === key);
          if (header) {
            header.restore();
          }
        });
      }

      this.updateStickyColumnRange();
    });
  },

  openRowDetail({ detailUrl }) {
    if (!detailUrl) return;

    if (this.openDetailsBySlidePage && this.slidePageAvailable()) {
      this.slidePage.requestPage(detailUrl);
    } else {
      this.dialogPage.requestPage(detailUrl);
    }
  },

  slidePageAvailable() {
    const viewportMinWidth = this.stickyColumnMinWidthOnMinimal
      + MINIMAL_COLUMNS_MIN_WIDTH + SLIDE_PAGE_WIDTH;
    return this.slideOpened
      || this.horizontalContainer.getBoundingClientRect().width >= viewportMinWidth;
  },

  handleGroupExpanded(event) {
    if (!event.detail.expanded) {
      let nextSibling = event.target.nextElementSibling;
      while (nextSibling && !nextSibling.expanded) {
        nextSibling = nextSibling.nextElementSibling;
      }

      if (nextSibling) {
        this.verticalContainer.scrollTop = nextSibling.offsetTop;
      }
    }
  },

  handleGroupCreated() {
    this.recreateRowSortable();
  },

  handleKeydown(event) {
    if (!this.currentSelectedRow
      || matches(event.target, 'input')
      || matches(event.target, 'textarea')
      || closest(event.target, 'form')
      || closest(event.target, 'button')
      || closest(event.target, '[contenteditable]')) return;

    const arrowUp = event.key === 'ArrowUp' || event.keyCode === 38;
    const arrowDown = event.key === 'ArrowDown' || event.keyCode === 40;
    const isEnter = event.key === 'Enter' || event.keyCode === 13;
    if (arrowUp || arrowDown || isEnter) {
      event.stopPropagation();
      event.preventDefault();
      this.clearSelectedRowsExceptCurrent();
    }

    if (arrowUp) {
      this.selectPreviousRow();
    } else if (arrowDown) {
      this.selectNextRow();
    } else if (isEnter) {
      this.currentSelectedRow
        .querySelector('tr-grid-todo-title-cell').beginEditing();
    }
  },

  selectPreviousRow() {
    let previousRow = this.currentSelectedRow.previousElementSibling;

    const findLastSubRow = (row) => {
      if (row && row.expanded) {
        return findLastSubRow(row.getSubGroup().getLastRow());
      }
      return row;
    };
    previousRow = findLastSubRow(previousRow);

    if (!previousRow) {
      const subGroup = this.currentSelectedRow.getParentSubGroup();
      if (subGroup) previousRow = subGroup.parentNode;
    }

    if (!previousRow) {
      const group = closest(this.currentSelectedRow, '.grid-group');
      if (!group) return;

      const previousGroup = group.previousElementSibling;
      if (previousGroup) {
        previousRow = previousGroup.querySelector('.group-content > tr-grid-todo-row:last-of-type');
        previousRow = findLastSubRow(previousRow);
      }
    }

    if (previousRow) {
      previousRow.selected = true;
      this.selectRow(previousRow);
      this.scrollIntoRow(previousRow);
      this.openRowDetail(previousRow);
    }
  },

  selectNextRow() {
    let nextRow;

    if (this.currentSelectedRow.expanded) {
      nextRow = this.currentSelectedRow.getSubGroup().querySelector('tr-grid-todo-row');
    } else {
      nextRow = this.currentSelectedRow.nextElementSibling;
    }

    if (!nextRow) {
      const subGroup = this.currentSelectedRow.getParentSubGroup();
      if (subGroup) {
        nextRow = subGroup.parentNode.nextElementSibling;
      }
    }

    if (!nextRow) {
      const group = closest(this.currentSelectedRow, '.grid-group', this);
      if (group) {
        const nextGroup = group.nextElementSibling;
        nextRow = nextGroup ? nextGroup.querySelector('tr-grid-todo-row') : null;
      }
    } else if (!matches(nextRow, 'tr-grid-todo-row') && matches(nextRow, '.grid-group')) {
      nextRow = nextRow.querySelector('tr-grid-todo-row');
    }

    if (nextRow) {
      nextRow.selected = true;
      this.selectRow(nextRow);
      this.scrollIntoRow(nextRow);
      this.openRowDetail(nextRow);
    }
  },

  scrollIntoRow(row) {
    const scrollContainer = this.verticalContainer;
    const { top: rowTop, height: rowHeight } = row.getBoundingClientRect();

    if (row.scrollIntoViewIfNeeded) {
      row.scrollIntoViewIfNeeded();
    } else {
      const top = row.offsetTop;
      const containerHeight = scrollContainer.clientHeight;

      const overTop = top < scrollContainer.scrollTop;
      const overBottom = (top + rowHeight) > (scrollContainer.scrollTop + containerHeight);

      if (overTop || overBottom) {
        scrollContainer.scrollTop = top - containerHeight / 2 + rowHeight / 2;
      }
    }

    if (this.headerSticky && rowTop - rowHeight <= this.getBoundingClientRect().top) {
      scrollContainer.scrollTop -= rowHeight;
    }
  },

  disconnected() {
    this.stopListening();
  },
});
